import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MdOutlineSwapVert } from 'react-icons/md';
import { isEqual } from 'lodash';
import {
  EditTabs, TrashIcon, GreenCheckIcon,
} from '../Icons/Icons';
import { withContextProvider } from '../../hoc';
import { DialogsProvider } from '../../providers';
import createEditKeyDownHandler from '../../utils/editKeyDownHandler';
import { useDialogs, useUnsavedChangesWarning } from '../../hooks';
import { closeDialogWithUnsavedChanges } from '../../utils/dialogs';

const EditableTable = ({
  dataSource, columns, isDeleteEnabled = false, onUpdate = null, onDelete = null, itemType, onSortChange, onRowClick = null, defaultSortOrder = { },
}) => {
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [tempData, setTempData] = useState(dataSource);
  const [originalData, setOriginalData] = useState(null);
  const [updatedRowIndex, setUpdatedRowIndex] = useState(null);
  const [isAscendent, setIsAscendent] = useState(defaultSortOrder?.order ? defaultSortOrder.order === 'asc' : true);
  const [rowErrors, setRowErrors] = useState({});
  const { openDialog, closeDialog } = useDialogs();
  const [sortField, setSortField] = useState(defaultSortOrder?.sortBy ?? null);

  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);

  const hasUnsavedChanges = editingRowIndex !== null
    && !isEqual(tempData[editingRowIndex], originalData);

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, originalData]);

  // browser warning
  useUnsavedChangesWarning(isUnsavedChanges);

  useEffect(() => {
    setTempData([...dataSource]);
  }, [dataSource]);

  const handleEdit = (rowIndex) => {
    setEditingRowIndex(rowIndex);
    setOriginalData({ ...tempData[rowIndex] });
  };

  const handleChange = (e, rowIndex, colKey) => {
    const updatedData = [...tempData];
    updatedData[rowIndex][colKey] = e.target.value;
    setTempData(updatedData);
  };

  const handleUpdateClick = async (e) => {
    e.stopPropagation();
    if (onUpdate && typeof onUpdate === 'function') {
      const rowId = tempData[editingRowIndex].id;
      const result = await onUpdate(tempData[editingRowIndex]);
      if (result.success) {
        setEditingRowIndex(null);
        setOriginalData(null);
        setIsUnsavedChanges(false);
        setUpdatedRowIndex(editingRowIndex);
        setRowErrors((prev) => ({ ...prev, [rowId]: null }));
        setTimeout(() => {
          setUpdatedRowIndex(null);
        }, 1500);
      } else if (result.error === 'duplicate') {
        setRowErrors((prev) => ({
          ...prev,
          [rowId]: 'This tag name is already in use.',
        }));
      } else {
        setRowErrors((prev) => ({
          ...prev,
          [rowId]: 'An error occurred. Please try again.',
        }));
      }
    }
  };

  const handleCancelClick = (e, rowIndex) => {
    e.stopPropagation();
    const updatedData = [...tempData];
    updatedData[rowIndex] = originalData;
    setTempData(updatedData);
    const rowId = updatedData[rowIndex].id;
    setEditingRowIndex(null);
    setRowErrors((prev) => ({ ...prev, [rowId]: null }));
    setIsUnsavedChanges(false);
  };

  const handleEditClick = (e, rowIndex) => {
    e.stopPropagation();
    if (editingRowIndex !== null && hasUnsavedChanges) {
      closeDialogWithUnsavedChanges(openDialog, closeDialog, () => {
        handleCancelClick(e, editingRowIndex);
        handleEdit(rowIndex);
      }, setIsUnsavedChanges);
      return;
    }
    handleEdit(rowIndex);
  };

  const confirmDelete = (rowIndexToDelete) => {
    if (rowIndexToDelete !== null) {
      const updatedData = [...tempData];
      const itemToDelete = updatedData[rowIndexToDelete];
      const itemId = itemToDelete.id;

      if (onDelete && typeof onDelete === 'function') {
        onDelete(itemId);
      }
    }
  };

  const handleDeleteElementModal = (e, rowIndex) => {
    e.stopPropagation();
    openDialog({
      message: [
        <p className="text-center leading-snug">
          Delete the selected <b>{itemType}</b>?
        </p>,
      ],
      show: true,
      actions: [
        {
          label: 'Cancel',
          variant: 'secondary',
          onClick: closeDialog,
        },
        {
          label: `Delete ${itemType.toLowerCase()}`,
          variant: 'primary',
          onClick: () => {
            confirmDelete(rowIndex);
            closeDialog();
          },
        },
      ],
    });
  };
  const renderActionButtons = (rowIndex) => {
    if (editingRowIndex === rowIndex) {
      return (
        <div className="h-full flex items-stretch justify-center space-x-2">
          <button
            type="button"
            onClick={handleUpdateClick}
            className="h-full bg-vamos-black text-vamos-white rounded-lg py-1 px-3 hover:bg-vamos-gray"
          >
            Update
          </button>
          <button
            type="button"
            onClick={(e) => handleCancelClick(e, rowIndex)}
            className="h-full border border-vamos-black text-vamos-black rounded-lg py-1 px-3 hover:bg-vamos-light-gray"
          >
            Cancel
          </button>
        </div>
      );
    }
    if (updatedRowIndex === rowIndex) {
      return (
        <div className="flex justify-center items-center space-x-2">
          <span>Updated</span>
          <GreenCheckIcon />
        </div>
      );
    }
    return (
      <div className="flex justify-center items-center space-x-2">
        <button type="button" id={`edit-tags-${tempData[rowIndex].id}`} onClick={(e) => handleEditClick(e, rowIndex)}>
          <EditTabs />
        </button>
        {isDeleteEnabled && (
          <button type="button" id={`delete-tags-${tempData[rowIndex].id}`} onClick={(e) => handleDeleteElementModal(e, rowIndex)}>
            <TrashIcon />
          </button>
        )}
      </div>
    );
  };

  const handleSortClick = (sortBy) => {
    let nextIsAscendent;

    if (sortBy !== sortField) {
      // Use default if provided, else fallback to ascending
      nextIsAscendent = true;
    } else {
      // Toggle for same field
      nextIsAscendent = !isAscendent;
    }
    setSortField(sortBy);
    setIsAscendent(nextIsAscendent);
    onSortChange(sortBy, nextIsAscendent ? 'asc' : 'desc');
  };

  return (
    <>
      <table className="w-full">
        <thead>
          <tr className="w-full text-md font-semibold tracking-wide h-13 text-left text-vamos-black bg-vamos-v-light-gray uppercase">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`group px-4 py-3 ${col.headerClassName || ''}`}
                style={{ width: col.width }}
              >
                <div id={`header-${col.key}`} className={`flex ${col.headerClassName || ''}`}>
                  {col.header}
                  {col.isSortable && (
                  <div className="w-fit ml-1 opacity-0  group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => handleSortClick(col.key)}
                      className="ml-2 focus:outline-none"
                    >
                      <MdOutlineSwapVert size={16} className="text-vamos-green1" />
                    </button>
                  </div>
                  )}
                </div>
              </th>
            ))}
            <th className="text-center" style={{ width: '15%' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {tempData.map((row, rowIndex) => (
            <tr
              className={`text-vamos-black h-13 even:bg-vamos-v-light-gray hover:bg-vamos-green2 w-full z-10 ${itemType === 'Tag' && row.sites > 0 && editingRowIndex !== rowIndex ? 'cursor-pointer' : 'cursor-default'}`}
              key={row.id}
              onClick={() => {
                if (editingRowIndex === rowIndex) return;
                onRowClick?.(row.id, row.sites);
              }}
            >
              {columns.map((col) => (
                <td
                  key={`col-${col.key}-${row.id}`}
                  className={`px-4 py-1 text-base font-normal ${col.className || ''}`}
                  style={{ width: col.width, fontWeight: col.fontWeight || 'normal' }}
                >
                  {editingRowIndex === rowIndex && col.isEditable ? (
                    <div className="flex flex-col py-4 align-top">
                      <input
                        type="text"
                        value={row[col.key]}
                        onChange={(e) => handleChange(e, rowIndex, col.key)}
                        // Enter submits; Escape cancels — keep this for keyboard accessibility
                        onKeyDown={createEditKeyDownHandler(
                          (e) => handleUpdateClick(e),
                          (e) => handleCancelClick(e, rowIndex),
                        )}
                        className={`rounded-md p-2 h-full w-full border-2 border-solid ${
                          rowErrors[row.id]
                            ? 'border-vamos-red'
                            : 'border-vamos-light-gray'
                        }`}
                      />
                      {rowErrors[row.id] && (
                        <span className="absolute mt-10 font-normal text-sm text-vamos-red">
                          {rowErrors[row.id]}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div id={`cell-${col.key}-${rowIndex}`} className={`flex items-center ${col.className || ''}`}>
                      {col.shouldDisplayIcon && col.icon && <col.icon className="mr-2" />}
                      {row[col.key]}
                    </div>
                  )}
                </td>
              ))}
              <td className="px-4 py-5 text-base font-normal h-13 w-1/5">
                {renderActionButtons(rowIndex)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

EditableTable.propTypes = {
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      isEditable: PropTypes.bool,
      width: PropTypes.string,
      isSortable: PropTypes.bool,
    }),
  ).isRequired,
  isDeleteEnabled: PropTypes.bool,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  itemType: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  defaultSortOrder: PropTypes.object,
};

export default withContextProvider(DialogsProvider)(EditableTable);
